import type { Node } from '@tiptap/pm/model'
import type {
  TChapterBlock,
  TChapterInsertPlan,
  TChapterIssue,
  TChapterPlanItem,
  TChapterProposal,
} from '@/types/audiobookChapters'

export const introTitle = 'Introduction'
export const maxChapters = 300
export const chapterTitleMaxLength = 120
export const charsPerMinute = 900
export const warnMinutes = 100
export const hardLimitMinutes = 120
export const shortChapterSeconds = 30

export function blocksFromDoc(doc: Node): TChapterBlock[] {
  const blocks: TChapterBlock[] = []
  doc.forEach((child, pos) => {
    if (child.type.name === 'audiobookChapter') {
      blocks.push({ type: 'chapter', pos, text: '', title: String(child.attrs.title ?? '') })

      return
    }
    blocks.push({
      type: 'paragraph',
      pos,
      text: child.textContent,
      voice: String(child.attrs.voice ?? ''),
      level: Number(child.attrs.level ?? 0) || undefined,
    })
  })

  return blocks
}

export function chapterPlanFromBlocks(blocks: TChapterBlock[]): TChapterPlanItem[] {
  if (!blocks.length) return []

  const plan: TChapterPlanItem[] = []
  let current: TChapterPlanItem | null = null

  const open = (title: string, markerPos: number | null) => {
    current = { index: plan.length + 1, title, markerPos, chars: 0, blocks: 0, empty: true, voiceless: false }
    plan.push(current)
  }

  for (const block of blocks) {
    if (block.type === 'chapter') {
      open(block.title ?? '', block.pos)
      continue
    }
    if (!current) open(introTitle, null)
    const chapter = current!
    const text = block.text.trim()
    chapter.blocks++
    chapter.chars += text.length
    if (text !== '') {
      chapter.empty = false
      if (!block.voice) chapter.voiceless = true
    }
  }

  if (plan.length === 1 && plan[0].markerPos === null) plan[0].title = ''

  return plan
}

/**
 * QA-011: the first chapter break a user places anywhere in a document that
 * had none also gets a visible, real "Introduction" marker at the very start,
 * so chapter 1 is not panel-only. Not needed when the first break already
 * sits at position 0, or when the document already has chapter markers.
 * `introNodeSize` is the size of the marker node in the document (from
 * `editor.schema.nodes.audiobookChapter.create(...).nodeSize`), used to shift
 * every requested position that comes after the inserted marker.
 */
export function planChapterInsertions(
  hasMarkers: boolean,
  introNodeSize: number,
  requested: TChapterProposal[],
): TChapterInsertPlan {
  if (!requested.length) return { introAt: null, proposals: [] }
  if (hasMarkers || requested[0].pos === 0) return { introAt: null, proposals: requested }

  return {
    introAt: 0,
    proposals: requested.map((proposal) => ({ ...proposal, pos: proposal.pos + introNodeSize })),
  }
}

export function headingLevelsOf(blocks: TChapterBlock[]): number[] {
  const levels = new Set<number>()
  for (const block of blocks) {
    if (block.type === 'paragraph' && block.level && block.text.trim() !== '') levels.add(block.level)
  }

  return [...levels].sort((a, b) => a - b)
}

export function proposeChaptersFromHeadings(blocks: TChapterBlock[], level: number): TChapterProposal[] {
  const proposals: TChapterProposal[] = []
  let previous: TChapterBlock | null = null

  for (const block of blocks) {
    const isHeading = block.type === 'paragraph' && block.level === level && block.text.trim() !== ''
    if (isHeading && previous?.type !== 'chapter') {
      proposals.push({ pos: block.pos, title: block.text.trim().slice(0, chapterTitleMaxLength) })
    }
    previous = block
  }

  return proposals
}

export function proposeChapterTitle(text: string): string {
  const firstSentence = text.trim().split(/(?<=[.!?…])\s+/)[0] ?? ''
  const words = firstSentence.split(/\s+/).filter(Boolean)
  let title = words.slice(0, 8).join(' ')
  if (title.length > 60) title = title.slice(0, 60).replace(/\s+\S*$/, '')

  return title.replace(/[\s.,;:!?…-]+$/, '')
}

export function estimateMinutes(chars: number): number {
  return chars / charsPerMinute
}

export function estimateMs(chars: number): number {
  return Math.round(estimateMinutes(chars) * 60_000)
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60) % 60
  const hours = Math.floor(totalSeconds / 3600)
  const mm = String(minutes).padStart(2, '0')
  const ss = String(seconds).padStart(2, '0')

  return hours ? `${hours}:${mm}:${ss}` : `${minutes}:${ss}`
}

function label(chapter: TChapterPlanItem): string {
  return `Chapter ${chapter.index}${chapter.title ? ` "${chapter.title}"` : ''}`
}

/**
 * What stands between the chapters and a publish (blocks) or deserves a look (warnings).
 * Real durations, when known, replace the estimate from the character count.
 */
export function chapterIssues(plan: TChapterPlanItem[], durationsMs: (number | null)[] = []): TChapterIssue[] {
  const issues: TChapterIssue[] = []

  if (plan.length > maxChapters) {
    issues.push({
      level: 'block',
      code: 'too_many',
      message: `This audiobook has ${plan.length} chapters; the limit is ${maxChapters}. Remove some chapter breaks.`,
    })
  }

  plan.forEach((chapter, i) => {
    if (chapter.empty) {
      issues.push({
        level: 'block',
        code: 'empty',
        chapterIndex: chapter.index,
        message: `${label(chapter)} is empty. Remove one of the two chapter breaks in a row, or add text between them.`,
      })

      return
    }
    if (chapter.voiceless) {
      issues.push({
        level: 'block',
        code: 'voiceless',
        chapterIndex: chapter.index,
        message: `${label(chapter)} has text without a speaker. Pick a speaker for its paragraphs.`,
      })
    }

    const real = durationsMs[i] ?? null
    const ms = real ?? estimateMs(chapter.chars)
    const minutes = ms / 60_000
    const measured = real !== null ? 'is' : 'would be about'

    if (minutes > hardLimitMinutes) {
      issues.push({
        level: 'warn',
        code: 'over_limit',
        chapterIndex: chapter.index,
        message: `${label(chapter)} ${measured} ${Math.round(minutes)} minutes long; ACX takes at most ${hardLimitMinutes} minutes per file. Add a chapter break inside it before exporting for ACX.`,
      })
    } else if (minutes >= warnMinutes) {
      issues.push({
        level: 'warn',
        code: 'long',
        chapterIndex: chapter.index,
        message: `${label(chapter)} ${measured} ${Math.round(minutes)} minutes long, close to the ${hardLimitMinutes}-minute ACX limit per file.`,
      })
    } else if (plan.length > 1 && ms < shortChapterSeconds * 1000) {
      issues.push({
        level: 'warn',
        code: 'short',
        chapterIndex: chapter.index,
        message: `${label(chapter)} ${measured} under ${shortChapterSeconds} seconds long; ACX expects real chapters, not fragments.`,
      })
    }
  })

  return issues
}
